import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Button } from '@os/components/ui/button';
import { Separator } from '@os/components/ui/separator';
import { useToast } from '@os/components/ui/use-toast';
import { captureDesktopToPng, saveScreenshotToVFS, uploadScreenshotFromDataUrl, cropDataUrlToRect, getUserScreenshotsPath } from '@os/utils/screenshot';
import { Image as ImageIcon, Monitor, Share, MousePointerClick, Copy, CheckCircle2, AlertCircle, Link2, ExternalLink } from 'lucide-react';
import { ScrollArea } from '@os/components/ui/scroll-area';
import { FileSystemNavigator, ensureDir } from '@os/utils/filesystem';

// Add props so we can open Image Viewer
type ScreenshotToolProps = {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
};

const ScreenshotTool: React.FC<ScreenshotToolProps> = ({ onOpenWindow }) => {
  const { toast } = useToast();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // NEW: local navigator for listing screenshots folder
  const fsNavRef = useRef<FileSystemNavigator>(new FileSystemNavigator());

  const [lastPreview, setLastPreview] = useState<string | null>(null);
  const [lastVfsPath, setLastVfsPath] = useState<string | null>(null);
  const [lastShareLink, setLastShareLink] = useState<string | null>(null);
  const [lastFileName, setLastFileName] = useState<string | null>(null);
  const [lastTheme, setLastTheme] = useState<'light' | 'dark' | null>(null);

  // NEW: screenshot history state
  const [history, setHistory] = useState<Array<{ path: string; name: string; dataUrl: string }>>([]);

  // NEW: load screenshots history from VFS
  useEffect(() => {
    (async () => {
      await fsNavRef.current.waitForInitialization();
      const folder = await getUserScreenshotsPath();
      await ensureDir(folder);
      // List folder items and load PNGs
      const items = fsNavRef.current.getItemsInPath(folder) as Array<{ id: string; name: string; path: string; type: 'file' | 'folder' }>;
      const pngs = items
        .filter(i => i.type === 'file' && i.name.toLowerCase().endsWith('.png'))
        // Names include timestamp; sort newest first by name descending
        .sort((a, b) => b.name.localeCompare(a.name));

      const withData = await Promise.all(
        pngs.map(async (i) => {
          const item = fsNavRef.current.findItemByPath(i.path) as { content?: string } | null;
          const dataUrl = item?.content || '';
          return { path: i.path, name: i.name, dataUrl };
        })
      );
      setHistory(withData);
    })();
  }, []);

  const [isPicking, setIsPicking] = useState<boolean>(false);
  const pickHandlerRef = useRef<(ev: MouseEvent) => void>();

  const notifySaved = (vfsPath: string) => {
    toast({
      title: 'Screenshot saved',
      description: 'Saved to Pictures\\Screenshots.',
    });
  };

  // Helper: temporarily hide this app's OS window container before desktop capture
  const withSelfWindowHidden = async <T,>(fn: () => Promise<T>): Promise<T> => {
    let windowContainer: HTMLElement | null = null;
    let el: HTMLElement | null = rootRef.current;
    let hops = 0;
    const maxHops = 30;

    while (el && hops < maxHops) {
      const style = window.getComputedStyle(el);
      const isWindowLike =
        (style.position === 'absolute' || style.position === 'fixed') &&
        (style.boxShadow !== 'none' || style.borderStyle !== 'none');
      if (isWindowLike) {
        windowContainer = el;
        break;
      }
      el = el.parentElement;
      hops++;
    }

    if (windowContainer) {
      const prevVisibility = windowContainer.style.visibility;
      windowContainer.style.visibility = 'hidden';
      try {
        const result = await fn();
        windowContainer.style.visibility = prevVisibility;
        return result;
      } catch (e) {
        windowContainer.style.visibility = prevVisibility;
        throw e;
      }
    } else {
      return await fn();
    }
  };

  const captureDesktop = useCallback(async () => {
    const pngDataUrl = await withSelfWindowHidden(async () => {
      if (rootRef.current) {
        rootRef.current.setAttribute('data-ignore-capture', 'true');
      }
      try {
        return await captureDesktopToPng();
      } finally {
        if (rootRef.current) {
          rootRef.current.removeAttribute('data-ignore-capture');
        }
      }
    });

    const saved = await saveScreenshotToVFS(pngDataUrl, 'desktop');
    setLastPreview(pngDataUrl);
    setLastVfsPath(saved.vfsPath);
    setLastFileName(saved.fileName);
    setLastTheme(saved.theme);
    setLastShareLink(null);

    // NEW: prepend to history
    setHistory(prev => [{ path: saved.vfsPath, name: saved.fileName, dataUrl: pngDataUrl }, ...prev]);

    notifySaved(saved.vfsPath);
  }, []);

  // Begin window picking: user clicks any window to capture its element (never allow capturing this app)
  const startWindowPick = useCallback(() => {
    if (isPicking) return;
    setIsPicking(true);

    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '100000';
    overlay.style.background = 'rgba(0,0,0,0.35)';
    overlay.style.cursor = 'crosshair';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.innerHTML = `<div style="color:white; font-weight:600; background:rgba(0,0,0,0.6); padding:12px 16px; border-radius:8px;">Click a window to capture</div>`;
    document.body.appendChild(overlay);

    const onClick = async (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();

      // Find the element beneath the overlay at the click point
      overlay.style.pointerEvents = 'none';
      const below = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
      overlay.style.pointerEvents = 'auto';
      const target = (below || ev.target) as HTMLElement;

      try {
        // Prefer capturing the full MapleOS window container
        let winEl = target.closest('[data-window-id]') as HTMLElement | null;

        // Never capture the Screenshots app window
        if (winEl && (winEl.getAttribute('data-app-id') === 'screenshot-tool')) {
          toast({
            title: 'Cannot capture Screenshots window',
            description: 'Please click a different window.',
            variant: 'destructive',
          });
          return; // keep overlay active to allow another pick
        }

        // Fallback: try to ascend until we find a window container
        if (!winEl) {
          let el: HTMLElement | null = target;
          const maxHops = 20;
          let hops = 0;
          while (el && hops < maxHops) {
            const wnd = el.closest('[data-window-id]') as HTMLElement | null;
            if (wnd) { winEl = wnd; break; }
            el = el.parentElement;
            hops++;
          }
        }

        if (!winEl) {
          toast({
            title: 'Capture failed',
            description: 'No window found at that location.',
            variant: 'destructive',
          });
          return;
        }

        const dpr = Math.max(1, window.devicePixelRatio || 1);

        // Hide the overlay and this tool's OS window container during capture
        const desktopPng = await withSelfWindowHidden(async () => {
          const prevOverlayVis = overlay.style.visibility;
          overlay.style.visibility = 'hidden';
          try {
            return await captureDesktopToPng();
          } finally {
            overlay.style.visibility = prevOverlayVis;
          }
        });

        const rect = winEl.getBoundingClientRect();
        const windowPng = await cropDataUrlToRect(desktopPng, rect, dpr);

        const saved = await saveScreenshotToVFS(windowPng, 'window');

        setLastPreview(windowPng);
        setLastVfsPath(saved.vfsPath);
        setLastFileName(saved.fileName);
        setLastTheme(saved.theme);
        setLastShareLink(null);

        // NEW: prepend to history
        setHistory(prev => [{ path: saved.vfsPath, name: saved.fileName, dataUrl: windowPng }, ...prev]);

        notifySaved(saved.vfsPath);
      } catch {
        toast({
          title: 'Capture failed',
          description: 'Could not capture that window. Try again or use Desktop capture.',
          variant: 'destructive',
        });
      } finally {
        if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
        setIsPicking(false);
        if (pickHandlerRef.current) {
          document.removeEventListener('click', pickHandlerRef.current, true);
          pickHandlerRef.current = undefined;
        }
      }
    };

    pickHandlerRef.current = onClick;
    document.addEventListener('click', onClick, true);
  }, [isPicking, toast]);

  // Create link: upload last preview and auto-copy
  const createLink = useCallback(async () => {
    if (!lastPreview || !lastFileName || !lastTheme) return;
    const { signedUrl } = await uploadScreenshotFromDataUrl(lastPreview, lastFileName, lastTheme);
    setLastShareLink(signedUrl || null);

    if (signedUrl) {
      try {
        await navigator.clipboard.writeText(signedUrl);
        toast({ title: 'Link copied', description: 'Shareable link is on your clipboard.' });
      } catch {
        toast({ title: 'Link ready', description: 'Copy from Details.', variant: 'default' });
      }
    }
  }, [lastPreview, lastFileName, lastTheme, toast]);

  // Open in MapleOS (Image Viewer)
  const openInOS = useCallback(() => {
    if (!onOpenWindow || !lastVfsPath || !lastFileName || !lastPreview) return;
    onOpenWindow('image-viewer', `Image Viewer - ${lastFileName}`, lastVfsPath, lastPreview);
  }, [onOpenWindow, lastVfsPath, lastFileName, lastPreview]);

  // NEW: open a history tile
  const openHistoryItem = useCallback((item: { path: string; name: string; dataUrl: string }) => {
    if (!onOpenWindow) return;
    onOpenWindow('image-viewer', `Image Viewer - ${item.name}`, item.path, item.dataUrl);
  }, [onOpenWindow]);

  return (
    <div ref={rootRef} data-ignore-capture="true" className="h-full w-full p-4">
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Screenshots
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={captureDesktop} variant="default" className="gap-2">
              <Monitor className="w-4 h-4" />
              Capture Desktop
            </Button>
            <Button onClick={startWindowPick} variant="outline" className="gap-2">
              <MousePointerClick className="w-4 h-4" />
              Capture a Window
            </Button>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded border p-3">
              <div className="text-sm font-medium mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Last Capture
              </div>
              {lastPreview ? (
                <img
                  src={lastPreview}
                  alt="Last screenshot"
                  className="w-full h-auto rounded border"
                />
              ) : (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  No screenshots yet.
                </div>
              )}
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium mb-2">Details</div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Saved to:</span>
                  <div className="mt-1 font-mono break-all">{lastVfsPath || '—'}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Share link:</span>
                  <div className="mt-1 font-mono break-all">{lastShareLink || '—'}</div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="gap-2"
                    disabled={!lastPreview || !!lastShareLink}
                    onClick={createLink}
                  >
                    <Link2 className="w-4 h-4" />
                    Create link
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    disabled={!lastShareLink}
                    onClick={async () => {
                      if (!lastShareLink) return;
                      try {
                        await navigator.clipboard.writeText(lastShareLink);
                        toast({ title: 'Link copied' });
                      } catch {
                        toast({ title: 'Copy failed', variant: 'destructive' });
                      }
                    }}
                  >
                    <Copy className="w-4 h-4" />
                    Copy link
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    disabled={!lastPreview || !lastVfsPath}
                    onClick={openInOS}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in MapleOS
                  </Button>

                  {lastShareLink && (
                    <a href={lastShareLink} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="default" className="gap-2">
                        <Share className="w-4 h-4" />
                        Open link
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* NEW: History grid */}
          <div className="rounded border p-3">
            <div className="text-sm font-medium mb-2">History</div>
            <ScrollArea className="max-h-80 rounded">
              {history.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {history.map((item) => (
                    <button
                      key={item.path}
                      className="group text-left"
                      onClick={() => openHistoryItem(item)}
                      title={item.name}
                    >
                      <div className="relative w-full aspect-video overflow-hidden rounded border">
                        <img
                          src={item.dataUrl}
                          alt={item.name}
                          className="w-full h-full object-cover transition-transform group-hover:scale-[1.02]"
                        />
                      </div>
                      <div className="mt-1 text-xs font-mono break-all text-muted-foreground">
                        {item.name}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  No screenshots saved yet.
                </div>
              )}
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScreenshotTool;